import { useMemo } from 'react';
import { useOrganization, type Organization } from '../../../contexts/OrganizationContext';
import { usePartnerCrmGrant } from '../../partner/hooks/usePartnerCrmGrant';

export interface SubscriptionLockInfo {
    isBlocked: boolean;
    status: 'past_due' | 'canceled' | 'blocked' | 'trial_expired' | 'no_plan' | null;
    daysUntilBlock?: number;
}

const FREE: SubscriptionLockInfo = { isBlocked: false, status: null, daysUntilBlock: undefined };
const NO_PLAN: SubscriptionLockInfo = { isBlocked: true, status: 'no_plan', daysUntilBlock: 0 };

/**
 * Regra pura de bloqueio por assinatura.
 * plan é sempre um plano pago (CHECK no banco); quem decide acesso é subscription_status.
 */
export function resolveSubscriptionLock(
    org: Organization | null,
    isLoading: boolean,
    now: Date = new Date(),
    options?: { partnerCrmGranted?: boolean },
): SubscriptionLockInfo {
    if (options?.partnerCrmGranted && org) {
        return FREE;
    }

    if (!org) {
        // Carregando: isLoading controla a UI. Carregado e sem org: bloquear.
        return isLoading ? FREE : NO_PLAN;
    }

    const { subscriptionStatus, trialEndsAt, blockedAt, status } = org;

    // PRIORIDADE 1: blocked_at no passado bloqueia sempre (banco pode ter inconsistência)
    if (blockedAt) {
        if (now >= blockedAt) {
            return { isBlocked: true, status: 'blocked', daysUntilBlock: 0 };
        }
        const daysUntilBlock = Math.max(0, Math.ceil((blockedAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
        if (subscriptionStatus === 'past_due') {
            return { isBlocked: false, status: 'past_due', daysUntilBlock };
        }
        if (subscriptionStatus === 'canceled') {
            return { isBlocked: false, status: 'canceled', daysUntilBlock };
        }
    }

    // Assinatura ativa ou trial local vigente libera
    if (subscriptionStatus === 'active' || subscriptionStatus === 'trialing') {
        if (subscriptionStatus === 'trialing' && trialEndsAt && now > trialEndsAt) {
            return { isBlocked: true, status: 'trial_expired', daysUntilBlock: 0 };
        }
        return FREE;
    }

    if (status === 'suspended') {
        return { isBlocked: true, status: 'blocked', daysUntilBlock: 0 };
    }

    if (status === 'cancelled') {
        return { isBlocked: true, status: 'canceled', daysUntilBlock: 0 };
    }

    // Sem subscription_status: org nunca passou pelo fluxo de billing — bloquear até o checkout
    if (!subscriptionStatus) {
        return NO_PLAN;
    }

    if (subscriptionStatus === 'blocked') {
        return { isBlocked: true, status: 'blocked', daysUntilBlock: 0 };
    }

    if (subscriptionStatus === 'past_due') {
        // past_due sem blocked_at: grace period ainda não agendado — não bloquear
        return FREE;
    }

    if (subscriptionStatus === 'canceled') {
        return { isBlocked: true, status: 'canceled', daysUntilBlock: 0 };
    }

    return FREE;
}

export const useSubscriptionLock = (): SubscriptionLockInfo & { isLoading: boolean } => {
    const { currentOrganization, isLoading } = useOrganization();
    const partnerCrmGranted = usePartnerCrmGrant();

    const blockInfo = useMemo(
        () => resolveSubscriptionLock(currentOrganization, isLoading, new Date(), { partnerCrmGranted }),
        [currentOrganization, isLoading, partnerCrmGranted],
    );

    return { ...blockInfo, isLoading };
};
